"use client";

import React, { useEffect, useState, useRef } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import {
  useFetchNewsArticle,
  useFetchNewsArticles,
} from "@/apis/queryFunctions/news";
import appendImageUrlFromAPI from "@/utils/appendImageUrlFromAPI";
import sanitizeDOMData from "@/utils/sanitizeDOMData";
import { formatProductContent } from "@/utils/stripHTML";
import { useTranslatedText } from "@/hooks/useProductTranslation";
import useWindowSize from "@/hooks/useWindowSize";
import styles from "./newsDetail.module.css";
import AppBreadcrumbs from "@/components/Common/AppBreadcrumbs";
import Footer from "@/components/Layouts/Footer/index";
import { useHeader } from "@/context/HeaderLayoutContext";
import { useNewsPageData } from "@/hooks/useNewsBanner";
import slugify from "@/utils/slugify";

function NewsCardTitle({ title, locale }: { title: string; locale: string }) {
  const displayTitle = useTranslatedText(title, locale);
  return <>{displayTitle}</>;
}

export default function NewsDetailClient({
  slug,
  locale,
  initialArticle,
}: {
  slug: string;
  locale: string;
  initialArticle?: any;
}) {
  const router = useRouter();
  const { isMobile, isTablet } = useWindowSize();
  const [notFound, setNotFound] = useState(false);
  const { data: clientDetail, isFetched } = useFetchNewsArticle(slug, locale) as any;
  const detail = initialArticle || clientDetail;
  const displayTitle = useTranslatedText(detail?.title || "", locale);

  const { setHeaderStyle } = useHeader();

  useEffect(() => {
    setHeaderStyle({ transparent: true });
    return () => setHeaderStyle({ transparent: false });
  }, [setHeaderStyle]);

  const { data: news } = useFetchNewsArticles({
    limit: 6,
    page: 1,
    condition: {
      ngon_ngu: locale,
      loai_tin_tuc: ["Tin tức", "Tin tức Việt Thiên", "Tin tức cà phê"],
    },
  });

  useEffect(() => {
    if (isFetched && (!detail || Object.keys(detail).length === 0)) {
      setNotFound(true);
    }
    if (isFetched && detail?.title) {
      setNotFound(false); // Reset if found later
      // Logic for IDs (TOC)
      const elms = document.querySelectorAll(
        ".post-content h1, .post-content h2, .post-content h3"
      );
      elms.forEach((elm) => {
        if (elm.textContent) {
          elm.setAttribute("id", slugify(elm.textContent));
        }
      });
    }
  }, [isFetched, detail]);

  useEffect(() => {
    if (notFound) {
      router.replace(`/${locale}`);
    }
  }, [notFound, router, locale]);

  if (notFound) {
    return null;
  }

  const breadcrumbs = [
    { title: locale === "vi" ? "Trang chủ" : "Home", href: `/${locale}` },
    { title: locale === "vi" ? "Tin tức" : "News", href: `/${locale}/tin-tuc` },
    { title: displayTitle || "...", href: "#" },
  ];

  const sameNews = news
    ? news.filter((n: any) => n.slug !== slug).slice(0, 5)
    : [];

  const { getBannerUrl } = useNewsPageData(locale);

  const bannerImage = React.useMemo(() => {
    const category = (detail?.loai_tin_tuc || "").toLowerCase();
    if (category.includes("cà phê") || category.includes("coffee")) {
      return getBannerUrl("banner-news-cafe", "/images/new_cafe.png");
    }
    if (category.includes("nông sản") || category.includes("giá")) {
      return getBannerUrl("banner-news-dau", "/images/new_dau.png");
    }
    if (category.includes("trà") || category.includes("tea")) {
      return getBannerUrl("banner-news-tra", "/images/new_banner.png");
    }
    return getBannerUrl("banner-news", "/images/new_banner.png");
  }, [detail?.loai_tin_tuc, getBannerUrl]);

  return (
    <div className={styles.newsWrapper} style={{ paddingTop: "0px" }}>
      {/* Hero Banner */}
      <section className={styles.hero}>
        <div className={styles.heroBg}>
          <Image
            src={bannerImage}
            alt={displayTitle || (locale === "vi" ? "Tin tức" : "News")}
            fill
            priority
            sizes="100vw"
            className={styles.bannerBgImage}
          />
          <div className={styles.heroMainContent}>
            <h1 className={styles.heroTitle}>
              {locale === "vi" ? "Tin tức" : "News"}
            </h1>
          </div>
        </div>
      </section>

      <div className={styles.container}>
        {/* Breadcrumbs */}
        <div style={{ marginTop: "20px", marginBottom: "20px" }}>
          <AppBreadcrumbs
            items={breadcrumbs}
            isLoading={!detail && !isFetched}
          />
        </div>

        <div className={styles.grid}>
          {/* Main Article */}
          <article className={styles.mainCol}>
            <div className={styles.titleWrapper}>
              {!detail ? (
                <div className={`${styles.skeleton} ${styles.skeletonTitle}`} />
              ) : (
                <h1
                  className={styles.title}
                  style={{ textTransform: "uppercase" }}
                >
                  {displayTitle}
                </h1>
              )}
            </div>

            <div className={styles.meta}>
              {!detail ? (
                <div className={`${styles.skeleton} ${styles.skeletonMeta}`} />
              ) : (
                <>
                  <span
                    className={styles.metaAuthor}
                    style={{ textTransform: "uppercase" }}
                  >
                    {detail.tac_gia ||
                      (locale === "vi" ? "VIỆT THIÊN" : "VIET THIEN")}
                  </span>
                  <span className={styles.metaDivider}>|</span>
                  <span>
                    {new Date(
                      detail.date_updated || detail.date_created || Date.now()
                    ).toLocaleDateString(locale, {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                    })}
                  </span>
                </>
              )}
            </div>

            <div
              className={`${styles.content} post-content`}
              suppressHydrationWarning
            >
              {detail?.content || detail?.noi_dung ? (
                sanitizeDOMData(
                  formatProductContent(detail.content || detail.noi_dung),
                  {}
                )
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  <div
                    className={`${styles.skeleton} ${styles.skeletonText}`}
                  />
                  <div
                    className={`${styles.skeleton} ${styles.skeletonText}`}
                  />
                  <div
                    className={`${styles.skeleton} ${styles.skeletonText}`}
                    style={{ width: "70%" }}
                  />
                  <div
                    className={`${styles.skeleton} ${styles.skeletonText}`}
                    style={{ width: "90%" }}
                  />
                </div>
              )}
            </div>
          </article>

          {/* Sidebar */}
          <aside className={styles.sideCol}>
            <h3
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "18px",
                marginBottom: "20px",
                color: "#fff",
                backgroundColor: "#2e5c42",
                padding: "12px 16px",
                width: "100%",
                fontWeight: 600,
              }}
            >
              {locale === "vi"
                ? "Có thể bạn quan tâm"
                : "You may be interested"}
            </h3>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "30px" }}
            >
              {sameNews.map((item: any) => (
                <a
                  key={item._id}
                  href={`/${locale}/tin-tuc/${item.slug}`}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                    textDecoration: "none",
                    color: "inherit",
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      height: "200px",
                      position: "relative",
                      borderRadius: "4px",
                      overflow: "hidden",
                    }}
                  >
                    <Image
                      src={appendImageUrlFromAPI({
                        src: item.picture,
                        size: "m",
                      })}
                      alt={item.title}
                      fill
                      style={{ objectFit: "cover" }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h4
                      style={{
                        fontSize: "15px",
                        fontWeight: 700,
                        margin: 0,
                        lineHeight: 1.4,
                        textTransform: "uppercase",
                        color: "#333",
                      }}
                    >
                      <NewsCardTitle title={item.title} locale={locale} />
                    </h4>
                  </div>
                </a>
              ))}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
